import { CheckCircleIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { PriceSuggestions, ValuationComparable, ValuationHistoryItem, ValuationPhoto, ValuationProperty, ValuationRooms } from '../../../@types/Valuation';
import type { RootState } from '../../../store/store';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import Spinner from '../../common/Spinner';
import ValuationComparables from './ValuationComparables';
import type { ValuationPropertyFormHandle } from './ValuationPropertyForm';
import ValuationPropertyForm from './ValuationPropertyForm';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onGenerated?: () => void;
  coproId: number;
  coproPostalCode: string;
  coproCity: string;
  coproAddress: string;
  initialData?: ValuationHistoryItem;
}

type Step = 'property' | 'comparables' | 'generating';

const DEFAULT_ROOMS: ValuationRooms = {
  bedroom: 0,
  livingroom: 0,
  kitchen: 0,
  bathroom: 0,
  wc: 0,
  garage: 0,
};

const DEFAULT_PROPERTY: ValuationProperty = {
  type: 'apartment',
  seller: '',
  description: '',
  rooms: 3,
  roomsDetail: { ...DEFAULT_ROOMS },
  area: undefined,
  dpe: undefined,
  year: undefined,
  heating: undefined,
  price: 0,
  priceMax: 0,
  strengths: [],
  weaknesses: [],
  photos: [],
};

const STEPS: Array<{ id: Step; label: string; num: number }> = [
  { id: 'property', label: 'Bien', num: 1 },
  { id: 'comparables', label: 'Comparables', num: 2 },
  { id: 'generating', label: 'Génération', num: 3 },
];

export default function ValuationModal({ isOpen, onClose, onGenerated, coproId, coproPostalCode, coproCity, coproAddress, initialData }: Props) {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const accentColor = company?.brandingSettings?.color1 || '#134094';

  const [step, setStep] = useState<Step>('property');
  const [property, setProperty] = useState<ValuationProperty>(DEFAULT_PROPERTY);
  const [selectedComparables, setSelectedComparables] = useState<ValuationComparable[]>([]);
  const [generating, setGenerating] = useState(false);
  const [priceSuggestions, setPriceSuggestions] = useState<PriceSuggestions | null>(null);
  const propertyFormRef = useRef<ValuationPropertyFormHandle>(null);

  useEffect(() => {
    if (!isOpen || !company) { return; }
    const controller = new AbortController();
    fetch(`/api/companies/${company.id}/copros/${coproId}/valuation/average-price`, {
      headers: { Authorization: JWTAuthorization(jwt) },
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: PriceSuggestions | null) => {
        if (data && data.copro && data.apartments && data.houses) {
          setPriceSuggestions(data);
        } else {
          setPriceSuggestions(null);
        }
      })
      .catch(() => setPriceSuggestions(null));
    return () => { controller.abort(); };
  }, [isOpen, company, coproId, jwt]);

  useEffect(() => {
    if (isOpen && initialData) {
      const pd = initialData.propertyData;
      const baseProperty: ValuationProperty = {
        type: pd.type,
        seller: pd.seller ?? '',
        description: pd.description ?? '',
        rooms: pd.rooms,
        roomsDetail: pd.roomsDetail ?? { ...DEFAULT_ROOMS },
        area: pd.area,
        dpe: pd.dpe,
        year: pd.year,
        heating: pd.heating,
        price: pd.price,
        priceMax: pd.priceMax,
        strengths: pd.strengths ?? [],
        weaknesses: pd.weaknesses ?? [],
        photos: [],
      };
      setProperty(baseProperty);
      setSelectedComparables(initialData.comparables ?? []);
      setStep('property');

      const urls = initialData.photoUrls ?? [];
      if (urls.length > 0) {
        const controller = new AbortController();
        Promise.all(
          urls.map(async(url): Promise<ValuationPhoto | null> => {
            try {
              const resp = await fetch(url, {
                headers: { Authorization: JWTAuthorization(jwt) },
                signal: controller.signal,
              });
              if (!resp.ok) { return null; }
              const blob = await resp.blob();
              return { source: 'remote', url: URL.createObjectURL(blob) };
            } catch {
              return null;
            }
          }),
        ).then((results) => {
          if (controller.signal.aborted) { return; }
          const photos = results.filter((p): p is ValuationPhoto => p !== null);
          setProperty((prev) => ({ ...prev, photos }));
        });

        return () => { controller.abort(); };
      }
    } else if (isOpen && !initialData) {
      setProperty(DEFAULT_PROPERTY);
      setSelectedComparables([]);
      setStep('property');
    }

    return undefined;
  }, [isOpen, initialData, jwt]);

  const handleClose = () => {
    property.photos.forEach((p) => {
      if (p.source === 'local') { URL.revokeObjectURL(p.preview); }
      if (p.source === 'remote' && p.url.startsWith('blob:')) { URL.revokeObjectURL(p.url); }
    });
    setStep('property');
    setProperty(DEFAULT_PROPERTY);
    setSelectedComparables([]);
    setGenerating(false);
    setPriceSuggestions(null);
    onClose();
  };

  const handleGenerate = useCallback(async() => {
    if (!company) { return; }

    setStep('generating');
    setGenerating(true);

    try {
      const formData = new FormData();
      formData.append('type', property.type);
      formData.append('rooms', String(property.rooms));
      formData.append('price', String(property.price));
      formData.append('priceMax', String(property.priceMax));
      formData.append('seller', property.seller);
      formData.append('description', property.description);
      formData.append('roomsDetail', JSON.stringify(property.roomsDetail));
      formData.append('compareWith', JSON.stringify(selectedComparables));
      formData.append('strengths', JSON.stringify(property.strengths));
      formData.append('weaknesses', JSON.stringify(property.weaknesses));

      if (property.area !== undefined) { formData.append('area', String(property.area)); }
      if (property.dpe) { formData.append('dpe', property.dpe); }
      if (property.year) { formData.append('year', property.year); }
      if (property.heating) { formData.append('heating', property.heating); }

      const resolvedRemoteBlobs = await Promise.all(
        property.photos.map((photo) =>
          photo.source === 'remote' && photo.url.startsWith('blob:')
            ? fetch(photo.url).then((r) => r.blob())
            : Promise.resolve(null),
        ),
      );

      let fileIdx = 0;
      property.photos.forEach((photo, idx) => {
        if (photo.source === 'local') {
          formData.append(`photos[${fileIdx}]`, photo.file);
          fileIdx++;
        } else if (resolvedRemoteBlobs[idx]) {
          formData.append(`photos[${fileIdx}]`, resolvedRemoteBlobs[idx], `existing-${fileIdx}.jpg`);
          fileIdx++;
        }
      });

      const url = `/api/companies/${company.id}/copros/${coproId}/valuation/generate`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: JWTAuthorization(jwt) },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        const message = errData?.message || `Erreur ${response.status}`;
        throw new Error(message);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `avis-de-valeur-${coproId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      a.remove();

      showToast('success', 'L\'avis de valeur a été généré avec succès.');
      onGenerated?.();
      handleClose();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Erreur lors de la génération.');
      setStep('comparables');
    } finally {
      setGenerating(false);
    }
  }, [company, coproId, jwt, property, selectedComparables]);

  const handlePropertyNext = useCallback(() => {
    if (propertyFormRef.current?.validate()) {
      setStep('comparables');
    }
  }, []);

  if (!company) { return null; }

  const currentStepIndex = STEPS.findIndex((s) => s.id === step);

  return (
    <Popup
      isOpen={isOpen}
      onClose={generating ? undefined : handleClose}
      title=""
      additionalClass="w-full max-w-4xl"
      width="90vw"
      maxWidth="56rem"
      height="85vh"
      maxHeight="85vh"
      disableCloseButton={generating}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Step indicator */}
        <div className="mb-5 flex items-center justify-center gap-2">
          {STEPS.map((s, idx) => {
            const done = idx < currentStepIndex;
            const active = idx === currentStepIndex;
            return (
              <React.Fragment key={s.id}>
                {idx > 0 && (
                  <div className={`h-0.5 w-10 rounded-full transition-colors ${idx <= currentStepIndex ? '' : 'bg-slate-200'}`} style={idx <= currentStepIndex ? { backgroundColor: accentColor } : undefined} />
                )}
                <div className="flex items-center gap-1.5">
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${active ? 'text-white shadow-sm' : done ? 'text-white' : 'bg-slate-200 text-slate-500'}`}
                    style={(active || done) ? { backgroundColor: accentColor } : undefined}
                  >
                    {done ? <CheckCircleIcon className="h-4 w-4" /> : s.num}
                  </div>
                  <span className={`text-xs font-medium ${active ? 'text-slate-800' : 'text-slate-400'}`}>{s.label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Copro context */}
        <div className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span className="font-medium text-slate-800">{coproAddress}</span> — {coproCity} {coproPostalCode}
        </div>

        {/* Step content — scrollable area */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {step === 'property' && (
            <ValuationPropertyForm
              ref={propertyFormRef}
              property={property}
              onChange={setProperty}
              accentColor={accentColor}
              priceSuggestions={priceSuggestions}
            />
          )}

          {step === 'comparables' && (
            <ValuationComparables
              companyId={company.id}
              coproId={coproId}
              property={property}
              postalCode={coproPostalCode}
              selected={selectedComparables}
              onSelectionChange={setSelectedComparables}
              onBack={() => setStep('property')}
              onNext={() => { handleGenerate(); }}
              accentColor={accentColor}
            />
          )}

          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center gap-5 py-16">
              <Spinner />
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">Génération de l&apos;avis de valeur en cours...</p>
                <p className="mt-1 text-xs text-slate-400">Cela peut prendre quelques instants.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer — fixed below scroll area */}
        {step === 'property' && (
          <div className="flex shrink-0 items-center justify-end border-t border-slate-200 pt-4">
            <button
              type="button"
              className="rounded-lg px-6 py-2.5 text-sm font-medium text-white shadow-sm transition hover:brightness-110"
              style={{ backgroundColor: accentColor }}
              onClick={handlePropertyNext}
            >
              Rechercher des comparables →
            </button>
          </div>
        )}
      </div>
    </Popup>
  );
}
